import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Announcement as AnnouncementIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  BeachAccess as BeachAccessIcon,
} from '@mui/icons-material';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { SkeletonLoader } from '../components/Common';
import { userService, dashboardService, activityService, announcementService, activityLogService, leaveRequestService } from '../services/apiService';
import { formatDate, getRelativeTime } from '../utils/dateFormat';
import { APP_CONFIG } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 280;

const DashboardHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([
    {
      title: 'Attendance Today',
      value: '0/0',
      icon: <CheckCircleIcon />,
      bgColor: '#1976d2', // Blue
      loading: true,
    },
    {
      title: 'Total Teachers',
      value: '0',
      icon: <SchoolIcon />,
      bgColor: '#9c27b0', // Purple
      loading: true,
    },
    {
      title: 'Active Classes',
      value: '0',
      icon: <AssignmentIcon />,
      bgColor: '#f57c00', // Orange
      loading: true,
    },
    {
      title: 'Next Event/Holiday',
      value: 'None',
      icon: <EventIcon />,
      bgColor: '#0097a7', // Teal/Cyan
      loading: true,
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLogs, setModalLogs] = useState([]);
  const [modalLogsLoading, setModalLogsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [teachersOnLeave, setTeachersOnLeave] = useState([]);
  const [nextEventOrHoliday, setNextEventOrHoliday] = useState(null);

  // Fetch activity logs function for dashboard (last 60 hours only)
  const fetchActivityLogs = useCallback(async () => {
    setActivityLogsLoading(true);
    try {
      // Calculate datetime 60 hours ago (60 hours * 60 minutes * 60 seconds * 1000 ms)
      const now = new Date();
      const sixtyHoursAgo = new Date(now.getTime() - 60 * 60 * 60 * 1000);
      // Format as YYYY-MM-DD HH:MM:SS for datetime filtering
      const fromDateTime = sixtyHoursAgo.toISOString().slice(0, 19).replace('T', ' ');
      
      const logsRes = await activityLogService.getAll({ 
        limit: 10, 
        offset: 0,
        from_date: fromDateTime
      });
      const logsData = logsRes.data || logsRes;
      setActivityLogs(logsData.logs || []);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
    } finally {
      setActivityLogsLoading(false);
    }
  }, []);

  // Fetch activity logs function for modal
  const fetchModalLogs = useCallback(async () => {
    setModalLogsLoading(true);
    try {
      const params = { limit: 100, offset: 0 };
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }
      const logsRes = await activityLogService.getAll(params);
      const logsData = logsRes.data || logsRes;
      setModalLogs(logsData.logs || []);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
    } finally {
      setModalLogsLoading(false);
    }
  }, [debouncedSearchQuery]);

  // Initial load for dashboard
  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  // Live mode interval effect for modal - only fetches activity logs API every 30 seconds
  useEffect(() => {
    if (!isLiveMode || !modalOpen) {
      return;
    }

    // Fetch immediately when live mode is enabled
    fetchModalLogs();

    // Set up interval to fetch every 30 seconds
    const interval = setInterval(() => {
      fetchModalLogs();
    }, 30000); // 30 seconds

    // Cleanup interval on unmount or when live mode is disabled
    return () => {
      clearInterval(interval);
    };
  }, [isLiveMode, modalOpen, fetchModalLogs]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load modal logs when modal opens or debounced search query changes
  useEffect(() => {
    if (modalOpen) {
      fetchModalLogs();
    }
  }, [modalOpen, debouncedSearchQuery, fetchModalLogs]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await dashboardService.get();
        const data = res.data || res || {};
        // Load upcoming holidays
        const today = new Date().toISOString().split('T')[0];
        const hRes = await activityService.getAll({ type: 'holiday', from: today });
        const list = (hRes.data || hRes || []).sort((a,b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
        setHolidays(list);
        const eRes = await activityService.getAll({ type: 'event', from: today });
        const evts = (eRes.data || eRes || []).sort((a,b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
        setEvents(evts);

        // Get next event or holiday (latest one)
        const allActivities = [...(list || []), ...(evts || [])];
        let nextEventHoliday = null;
        if (allActivities.length > 0) {
          const sorted = allActivities.sort((a, b) => new Date(a.date) - new Date(b.date));
          nextEventHoliday = sorted[0];
          setNextEventOrHoliday(nextEventHoliday);
        }

        // Load recent announcements
        const aRes = await announcementService.getAll();
        const anns = (aRes.data || aRes || []).slice(0, 6);
        setAnnouncements(anns);
        
        const next = [
          { title: 'Attendance Today', value: `${data.attendance_today ?? 0}/${data.total_students ?? 0}`, icon: <CheckCircleIcon />, bgColor: '#1976d2', loading: false, subtitle: data.students_present_today + ' students are present' }, // Blue
          { title: 'Total Teachers', value: String(data.total_teachers ?? 0), icon: <SchoolIcon />, bgColor: '#9c27b0', loading: false }, // Purple
          { title: 'Active Classes', value: String(data.active_classes ?? 0), icon: <AssignmentIcon />, bgColor: '#f57c00', loading: false }, // Orange
          { 
            title: 'Next Event/Holiday', 
            value: nextEventHoliday ? nextEventHoliday.name : 'None', 
            icon: <EventIcon />, 
            bgColor: '#0097a7', 
            loading: false,
            subtitle: nextEventHoliday ? formatDate(nextEventHoliday.date) : '',
          }, // Teal/Cyan
        ];
        setStats(next);
        
        // Get teachers on leave today and upcoming
        const leaveRes = await leaveRequestService.getAll({ 
          status: 'Approved',
          start_date: today 
        });
        const leaveData = Array.isArray(leaveRes?.data) ? leaveRes.data : (Array.isArray(leaveRes) ? leaveRes : []);
        setTeachersOnLeave(leaveData);
        
        // Activity logs are handled by the live mode effect above
      } catch (e) {
        setStats(s => s.map(x => ({ ...x, loading: false })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }


  return (
    <Box sx={{ 
      p: { xs: 2, sm: 2.5, md: 3 }, 
      background: (theme) => `linear-gradient(180deg, ${theme.palette.grey[50]}, ${theme.palette.background.default})` 
    }}>
      {/* Welcome Header */}
      {/* <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          }}
        >
          Welcome, {user?.full_name || 'Admin'}
        </Typography>
      </Box> */}

      {/* Statistics Cards */}
      {loading ? (
        <SkeletonLoader variant="dashboard" count={3} />
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: 'none',
                  background: `linear-gradient(135deg, ${stat.bgColor}15 0%, ${stat.bgColor}08 100%)`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${stat.bgColor}, ${stat.bgColor}80)`,
                  },
                  '&:hover': { 
                    boxShadow: `0 8px 30px ${stat.bgColor}40`, 
                    transform: 'translateY(-4px) scale(1.02)',
                    background: `linear-gradient(135deg, ${stat.bgColor}20 0%, ${stat.bgColor}12 100%)`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 3, md: 3.5 }, position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Icon at top */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, mb: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          width: { xs: 32, sm: 44, md: 48 },
                          height: { xs: 32, sm: 44, md: 48 },
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${stat.bgColor}, ${stat.bgColor}DD)`,
                          color: 'white',
                          boxShadow: `0 4px 12px ${stat.bgColor}40`,
                        }}
                      >
                        <Box sx={{ fontSize: { xs: 18, sm: 24, md: 26 }, display: 'flex', alignItems: 'center', color: 'white' }}>
                          {stat.icon}
                        </Box>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.9375rem', md: '1rem' },
                          fontWeight: 600,
                          color: stat.bgColor,
                          letterSpacing: '0.3px',
                        }}
                      >
                        {stat.title}
                      </Typography>
                    </Box>
                    {/* Value below */}
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{ 
                        fontSize: { xs: '1.25rem', sm: stat?.title === 'Next Event/Holiday' ? '1.25rem' : '2rem', md: stat?.title === 'Next Event/Holiday' ? '1.5rem' : '2.25rem' },
                        fontWeight: 800,
                        color: stat.bgColor,
                        lineHeight: 1.2,
                        mb: stat.subtitle ? 0.5 : 0,
                      }}
                    >
                      {stat.loading ? <CircularProgress size={20} sx={{ color: stat.bgColor }} /> : (stat?.value ?? '0')}
                    </Typography>
                    {stat.subtitle && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.8125rem', md: '0.875rem' },
                          color: 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {stat.subtitle}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Three-up row: Teachers on Leave, Announcements, Activity Logs */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mt: { xs: 2, md: 3 } }}>
        {/* Teachers on Leave */}
        <Grid item xs={12} sm={12} md={4}>
          <Paper sx={{ 
            p: { xs: 2.5, sm: 3, md: 3.5 }, 
            height: '100%', 
            borderRadius: 3, 
            border: 'none',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                }}
              >
                <BeachAccessIcon />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Teachers on Leave
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {teachersOnLeave.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <BeachAccessIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    No teachers on leave
                  </Typography>
                </Box>
              ) : (
                <>
                  {teachersOnLeave.map((leave, index) => (
                    <Box
                      key={leave.id || index}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          mb: 0.75,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          color: 'text.primary',
                        }}
                      >
                        {leave.teacher_name || 'Teacher'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        >
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </Typography>
                      </Box>
                      {leave.reason && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            lineHeight: 1.5,
                          }}
                        >
                          {leave.reason}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Paper sx={{ 
            p: { xs: 2.5, sm: 3, md: 3.5 }, 
            height: '100%', 
            borderRadius: 3, 
            border: 'none',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                  }}
                >
                  <AnnouncementIcon />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Announcements
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => navigate('/announcements')}
                variant="outlined"
              >
                View All
              </Button>
            </Box>
            <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto' }}>
              {announcements.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <AnnouncementIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    No announcements
                  </Typography>
                </Box>
              ) : (
                <>
                  {announcements.map((a, index) => (
                    <React.Fragment key={a.id}>
                      <Box
                        sx={{
                          p: 2.5,
                          mb: 1.5,
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#ff9800',
                              mt: 0.5,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                mb: 0.75,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                color: 'text.primary',
                              }}
                            >
                              {a.title || 'Announcement'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mb: 1,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                lineHeight: 1.6,
                              }}
                            >
                              {a.message}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.disabled',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                }}
                              >
                                {getRelativeTime(a.created_at || a.sent_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Paper sx={{ 
            p: { xs: 2.5, sm: 3, md: 3.5 }, 
            height: '100%', 
            borderRadius: 3, 
            border: 'none',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                  }}
                >
                  <AccessTimeIcon />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Recent Activity Logs
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setModalOpen(true)}
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  minWidth: { xs: 70, sm: 80 },
                  height: { xs: 24, sm: 28 },
                  textTransform: 'none',
                  px: { xs: 1, sm: 1.5 },
                }}
              >
                View More
              </Button>
            </Box>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {activityLogsLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : activityLogs.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    No activity logs found
                  </Typography>
                </Box>
              ) : (
                <>
                  {activityLogs.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: { xs: 0.75, sm: 1 },
                          bgcolor: 'grey.50',
                          borderRadius: 0.75,
                          mb: 0.75,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 500,
                              mb: 0.25,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              display: 'block',
                              lineHeight: 1.3,
                            }}
                          >
                            {log.desc || 'Activity logged'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 10 }} />
                            {getRelativeTime(log.created_at)}
                          </Typography>
                        </Box>
                        <Chip
                          label={log.action === 'C' ? 'C' : log.action === 'U' ? 'U' : 'D'}
                          size="small"
                          sx={{
                            bgcolor: log.action === 'C' ? 'success.main' : log.action === 'U' ? 'warning.main' : 'error.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                            height: { xs: 18, sm: 20 },
                            minWidth: { xs: 24, sm: 28 },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, sm: 0.75 },
                            },
                          }}
                        />
                      </Box>
                    </React.Fragment>
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Activity Logs Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsLiveMode(false);
          setSearchQuery('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Activity Logs
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant={isLiveMode ? 'contained' : 'outlined'}
                color={isLiveMode ? 'success' : 'primary'}
                onClick={() => setIsLiveMode(!isLiveMode)}
                sx={{
                  textTransform: 'none',
                  minWidth: 100,
                }}
              >
                {isLiveMode ? 'Live ON' : 'Show Live'}
              </Button>
              <IconButton
                size="small"
                onClick={() => {
                  setModalOpen(false);
                  setIsLiveMode(false);
                  setSearchQuery('');
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search activity logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Box>
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {modalLogsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : modalLogs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No activity logs found
                </Typography>
              </Box>
            ) : (
              <>
                {modalLogs.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          mb: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              fontSize: '0.875rem',
                              display: 'block',
                              lineHeight: 1.4,
                            }}
                          >
                            {log.desc || 'Activity logged'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontSize: '0.75rem',
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14 }} />
                            {getRelativeTime(log.created_at)}
                          </Typography>
                        </Box>
                        <Chip
                          label={log.action === 'C' ? 'Create' : log.action === 'U' ? 'Update' : 'Delete'}
                          size="small"
                          sx={{
                            bgcolor: log.action === 'C' ? 'success.main' : log.action === 'U' ? 'warning.main' : 'error.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                      {index < modalLogs.length - 1 && <Divider sx={{ my: 0.5 }} />}
                    </React.Fragment>
                  ))}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          ml: { xs: 0, md: `${0}px` },
          backgroundColor: 'background.default',
        }}
      >
        <Header onMenuClick={handleDrawerToggle} />
        <Box
          sx={{
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <DashboardHome />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
